import {ToggleButton} from '@wandb/components';
import React from 'react';

import {trackSetting} from '../../services/analytics/workspaceSettingsEvents';
import {type RunHistoryKeyInfo, type RunHistoryKeyType} from '../../types/run';
import {type PanelProps} from '../../util/panelProps';
import {type RunsLinePlotConfig} from '../PanelRunsLinePlot/types';
import {useTrackSettingPage} from '../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';

export type MetricsPickerConfig = Pick<
  RunsLinePlotConfig,
  'metricRegex' | 'metrics' | 'useMetricRegex'
>;

export type MetricsPickerProps = {
  options: string[];
  keyTypes: {
    [key: string]: RunHistoryKeyType;
  };
  keyInfo: RunHistoryKeyInfo;
  config: MetricsPickerConfig;
  updateConfig: PanelProps<RunsLinePlotConfig>['updateConfig'];
};

export const MetricsRegexToggle = ({
  updateConfig,
  config,
}: Pick<MetricsPickerProps, 'updateConfig' | 'config'>) => {
  const setMetricRegexMode = useSetMetricRegexMode({updateConfig, config});

  const onButtonClick = () => {
    setMetricRegexMode(!config.useMetricRegex);
  };
  const ariaLabel = config.useMetricRegex
    ? 'Disable regex matching'
    : 'Enable regex matching';

  return (
    <ToggleButton
      aria-label={ariaLabel}
      data-test="regex-button"
      icon="regex"
      isSelected={Boolean(config.useMetricRegex)}
      onPress={onButtonClick}
      variant="secondary"
      tooltip={{
        children: getRegexToggleTooltip(config.useMetricRegex),
        placement: 'top end',
      }}
    />
  );
};

function useSetMetricRegexMode({
  updateConfig,
  config,
}: Pick<MetricsPickerProps, 'updateConfig' | 'config'>) {
  const trackSettingPage = useTrackSettingPage();

  return (useMetricRegex: boolean) => {
    if (config.useMetricRegex === useMetricRegex) {
      return;
    }

    updateConfig({
      useMetricRegex,
    });

    trackSetting({
      action: 'change',
      page: trackSettingPage,
      settingLevel: 'panel',
      setting: 'yAxis',
      settingValue: useMetricRegex.toString(),
      additionalProperties: {
        useMetricRegex,
      },
    });
  };
}

function getRegexToggleTooltip(useMetricRegex?: boolean) {
  if (useMetricRegex) {
    return 'Regex matching is ON — plot all metrics matching the query';
  }
  return 'Regex matching is OFF — plot selected metrics only';
}
